import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  FiChevronRight,
  FiRefreshCw,
  FiPlus,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiCheckCircle,
  FiMessageSquare,
  FiServer,
} from "react-icons/fi";
import { useFetchVendorsQuery } from "../../../features/admin/vendor/vendorApi";
import {
  useGetSmsCredentialWithLimitOffsetMutation,
  useDeleteSmsCredentialMutation,
  useUpdateSmsCredentialStatusMutation,
} from "../../../features/admin/smsCredential/smsCredentialApi";
import {
  Page,
  PageHeader,
  Button,
  IconButton,
  SearchInput,
  Table,
  THead,
  Th,
  Td,
  EmptyRow,
  Badge,
  StatusDot,
  Modal,
} from "../../../components/ui";
import { getProvider, credentialFields, labelFor, isSecretField } from "./smsProviders";
import SmsCredentialEditor from "./SmsCredentialEditor";

const token = () => sessionStorage.getItem("access_token");
const ADMIN_KEY = "__admin__";

const SmsCredentialSetup = () => {
  const navigate = useNavigate();
  const [refetch, setRefetch] = useState(0);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [editor, setEditor] = useState(null); // { mode, record }
  const [viewer, setViewer] = useState(null); // credential row

  const { data: vendorsData } = useFetchVendorsQuery(token(), { refetchOnMountOrArgChange: true }) || {};
  const [getCreds, { data: credData, isLoading }] = useGetSmsCredentialWithLimitOffsetMutation();
  const [deleteSmsCredential] = useDeleteSmsCredentialMutation();
  const [updateStatus] = useUpdateSmsCredentialStatusMutation();

  useEffect(() => {
    getCreds({ token: token(), limit: 1000, offset: 0 });
  }, [getCreds, refetch]);

  const triggerRefetch = () => setRefetch((n) => n + 1);

  // ── group credentials by owner (admin/global + EVERY company) ────────────
  // Seed from the full company list so companies without any credential still
  // show up (blank) with an "Add for …" affordance.
  const groups = useMemo(() => {
    const creds = credData?.body?.data || [];
    const vendors = vendorsData?.body || [];
    const nameById = new Map(vendors.map((v) => [v.tenant_id, v.name]));
    const map = new Map();

    // admin / global always present
    map.set(ADMIN_KEY, { key: ADMIN_KEY, tenant_id: null, name: "Admin / Global", creds: [] });

    // one group per company, even with zero credentials
    vendors.forEach((v) => {
      if (!(v.tenant_id || v.tenant_id === 0)) return;
      if (!map.has(v.tenant_id)) {
        map.set(v.tenant_id, {
          key: v.tenant_id,
          tenant_id: v.tenant_id,
          name: v.name || `Tenant ${v.tenant_id}`,
          creds: [],
        });
      }
    });

    creds.forEach((c) => {
      const isAdmin = c.tenant_id === null || c.tenant_id === undefined;
      const gk = isAdmin ? ADMIN_KEY : c.tenant_id;
      if (!map.has(gk)) {
        map.set(gk, {
          key: gk,
          tenant_id: isAdmin ? null : c.tenant_id,
          name: isAdmin ? "Admin / Global" : nameById.get(c.tenant_id) || `Tenant ${c.tenant_id}`,
          creds: [],
        });
      }
      map.get(gk).creds.push(c);
    });

    return Array.from(map.values())
      .map((g) => ({
        ...g,
        creds: [...g.creds].sort((a, b) => a.type.localeCompare(b.type)),
        activeCount: g.creds.filter((c) => String(c.status) === "1").length,
      }))
      .sort((a, b) => {
        if (a.tenant_id === null) return -1;
        if (b.tenant_id === null) return 1;
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [credData, vendorsData]);

  // companies that can own a credential (have a tenant_id)
  const assignableCompanies = useMemo(
    () =>
      (vendorsData?.body || [])
        .filter((v) => v.tenant_id || v.tenant_id === 0)
        .map((v) => ({ tenant_id: v.tenant_id, name: v.name }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [vendorsData],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(
      (g) =>
        (g.name || "").toLowerCase().includes(q) ||
        String(g.tenant_id ?? "").includes(q) ||
        g.creds.some((c) => (c.type || "").toLowerCase().includes(q)),
    );
  }, [groups, search]);

  // ── actions ──────────────────────────────────────────────────────────────
  const setActive = async (row) => {
    if (String(row.status) === "1") return;
    try {
      await updateStatus({ token: token(), id: Number(row.id) }).unwrap();
      toast.success("Credential activated");
      triggerRefetch();
    } catch {
      toast.error("Could not change status");
    }
  };

  const setInactive = async (row) => {
    if (String(row.status) !== "1") return;
    try {
      await updateStatus({ token: token(), id: Number(row.id), body: { status: "2" } }).unwrap();
      toast.success("Credential deactivated");
      triggerRefetch();
    } catch {
      toast.error("Could not change status");
    }
  };

  const confirmDelete = (row) => {
    toast(
      (t) => (
        <span>
          <p className="font-semibold text-gray-800">Delete this credential?</p>
          <p className="text-xs text-gray-500 mt-0.5 capitalize">
            {row.type} · entity {row.entity_id}
          </p>
          <div className="flex gap-2 mt-3">
            <button
              className="px-3 py-1.5 text-xs rounded-lg bg-red-600 text-white hover:bg-red-700 font-medium"
              onClick={async () => {
                toast.dismiss(t.id);
                try {
                  await deleteSmsCredential({ token: token(), id: Number(row.id) }).unwrap();
                  toast.success("Deleted");
                  triggerRefetch();
                } catch {
                  toast.error("Delete failed");
                }
              }}
            >
              Delete
            </button>
            <button
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
              onClick={() => toast.dismiss(t.id)}
            >
              Cancel
            </button>
          </div>
        </span>
      ),
      { id: "deleteSmsCredential", duration: Infinity },
    );
  };

  return (
    <Page>
      <PageHeader
        title="SMS Credentials"
        subtitle="Credentials grouped by owner. One credential can be Active per owner — activating one deactivates the rest in that group."
        actions={
          <>
            <Button
              variant="outline"
              icon={<FiRefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />}
              onClick={triggerRefetch}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              icon={<FiPlus className="h-4 w-4" />}
              onClick={() => setEditor({ mode: "add" })}
            >
              Add SMS Credential
            </Button>
          </>
        }
      />

      <SearchInput
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search owner, ID or provider…"
        className="mb-3"
      />

      <Table>
        <THead>
          <tr>
            <th className="w-10 px-2 py-2.5" />
            <Th className="w-12">#</Th>
            <Th>Owner</Th>
            <Th>Credentials</Th>
            <Th>Active</Th>
          </tr>
        </THead>
        <tbody>
          {isLoading && filtered.length === 0 && <EmptyRow colSpan={5}>Loading credentials…</EmptyRow>}
          {!isLoading && filtered.length === 0 && <EmptyRow colSpan={5}>No credentials found.</EmptyRow>}

          {filtered.map((group, gi) => {
            const isOpen = !!expanded[group.key];
            return (
              <Fragment key={group.key}>
                <tr
                  onClick={() => setExpanded((prev) => ({ ...prev, [group.key]: !prev[group.key] }))}
                  className={`border-t border-gray-100 cursor-pointer hover:bg-gray-50/70 ${
                    isOpen ? "bg-gray-50/70" : ""
                  }`}
                >
                  <td className="px-2 py-2.5 text-center align-middle">
                    <FiChevronRight
                      className={`inline h-4 w-4 text-gray-400 transition-transform ${
                        isOpen ? "rotate-90" : ""
                      }`}
                    />
                  </td>
                  <Td className="text-gray-400 text-xs">{gi + 1}</Td>
                  <Td className="font-medium text-gray-900">
                    <span className="inline-flex items-center gap-2">
                      <FiServer className="h-4 w-4 text-gray-400" />
                      {group.name}
                      {group.tenant_id !== null && (
                        <span className="text-xs font-normal text-gray-400">(ID: {group.tenant_id})</span>
                      )}
                    </span>
                  </Td>
                  <Td className="text-gray-600">{group.creds.length}</Td>
                  <Td>
                    {group.creds.length === 0 ? (
                      <StatusDot tone="gray" label="not set up" />
                    ) : group.activeCount > 0 ? (
                      <StatusDot tone="green" label="1 active" />
                    ) : (
                      <StatusDot tone="amber" label="none active" />
                    )}
                  </Td>
                </tr>

                {isOpen && (
                  <tr className="border-t border-gray-100 bg-gray-50/40">
                    <td colSpan={5} className="px-4 md:px-6 py-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-400">
                          {group.creds.length} credential{group.creds.length === 1 ? "" : "s"}
                        </span>
                        <Button
                          size="sm"
                          variant="dashed"
                          icon={<FiPlus className="h-3.5 w-3.5" />}
                          onClick={() => setEditor({ mode: "add", initialTenantId: group.tenant_id })}
                        >
                          Add for {group.name}
                        </Button>
                      </div>
                      {group.creds.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-400">
                          No SMS credentials set up yet.
                        </div>
                      ) : (
                      <Table>
                        <THead>
                          <tr>
                            <Th className="w-12">#</Th>
                            <Th>Provider</Th>
                            <Th>Entity ID</Th>
                            <Th>Created</Th>
                            <Th>Status</Th>
                            <Th className="text-right">Actions</Th>
                          </tr>
                        </THead>
                        <tbody>
                          {group.creds.map((c, ci) => {
                            const active = String(c.status) === "1";
                            return (
                              <tr key={c.id} className="border-t border-gray-100">
                                <Td className="text-gray-400 text-xs">{ci + 1}</Td>
                                <Td className="capitalize font-medium text-gray-800">{c.type}</Td>
                                <Td className="text-gray-600">{c.entity_id || "—"}</Td>
                                <Td className="whitespace-nowrap">
                                  <div className="text-gray-500">
                                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                                  </div>
                                  {c.created_by_name && (
                                    <div className="text-xs text-gray-400">{c.created_by_name}</div>
                                  )}
                                </Td>
                                <Td>
                                  {active ? (
                                    <div className="flex items-center gap-2">
                                      <Badge tone="green">
                                        <FiCheckCircle className="h-3.5 w-3.5" /> Active
                                      </Badge>
                                      <Button size="xs" variant="ghost" onClick={() => setInactive(c)}>
                                        Set inactive
                                      </Button>
                                    </div>
                                  ) : (
                                    <Button size="xs" variant="outline" onClick={() => setActive(c)}>
                                      Set active
                                    </Button>
                                  )}
                                </Td>
                                <Td>
                                  <div className="flex items-center justify-end gap-1.5">
                                    <Button
                                      size="xs"
                                      variant="outline"
                                      icon={<FiEye className="h-3.5 w-3.5" />}
                                      onClick={() => setViewer(c)}
                                    >
                                      View
                                    </Button>
                                    <Button
                                      size="xs"
                                      variant="outline"
                                      icon={<FiMessageSquare className="h-3.5 w-3.5" />}
                                      onClick={() => navigate("/messageTemplate", { state: c })}
                                    >
                                      Templates
                                    </Button>
                                    <Button
                                      size="xs"
                                      variant="soft"
                                      icon={<FiEdit2 className="h-3.5 w-3.5" />}
                                      onClick={() => setEditor({ mode: "edit", record: c })}
                                    >
                                      Edit
                                    </Button>
                                    <IconButton
                                      tone="red"
                                      icon={<FiTrash2 className="h-3.5 w-3.5" />}
                                      onClick={() => confirmDelete(c)}
                                      className="border border-gray-200"
                                    />
                                  </div>
                                </Td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </Table>

      {/* add / edit drawer */}
      {editor && (
        <SmsCredentialEditor
          mode={editor.mode}
          record={editor.record}
          companies={assignableCompanies}
          existingCreds={credData?.body?.data || []}
          initialTenantId={editor.initialTenantId ?? null}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            triggerRefetch();
          }}
        />
      )}

      {/* view credential modal */}
      {viewer && (
        <Modal
          size="sm"
          onClose={() => setViewer(null)}
          title={<span className="capitalize">{viewer.type} credential</span>}
          subtitle={`Entity ID: ${viewer.entity_id || "—"}`}
        >
          <CredentialFields record={viewer} />
        </Modal>
      )}
    </Page>
  );
};

// masked key/value view of a credential's api_credential fields
const CredentialFields = ({ record }) => {
  const provider = getProvider(record.type);
  const fields = credentialFields(provider);
  const entries = fields.length
    ? fields.map((f) => [labelFor(provider, f), record.api_credential?.[f], isSecretField(f)])
    : Object.entries(record.api_credential || {}).map(([k, v]) => [k, v, isSecretField(k)]);

  return (
    <div className="space-y-2">
      {entries.map(([label, value, secret], i) => (
        <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2">
          <span className="text-xs font-medium text-gray-500">{label}</span>
          <span className="text-sm font-mono text-gray-800 truncate">
            {secret ? "••••••••" : String(value ?? "—")}
          </span>
        </div>
      ))}
    </div>
  );
};

export default SmsCredentialSetup;
